import { ApplicationScope } from "../types/App";

type PageDirectoryEntry = (entityId?: string, secondaryId?: string) => string;

export type PageDirectoryEntryName =
  | "home"
  | "initialUser"
  | "userDirectDebit"
  | "employerHome"
  | "employerApproveDrivers"
  | "employerInvoices"
  | "employerPackagingAgreements"
  | "employerPayrollAdvice"
  | "employerFBTReports"
  | "employerEngagementReports"
  | "employerPromotions"
  | "employerLuxuryLeaseReports"
  | "employerViewDossiers"
  | "employerTerminateDossier"
  | "employerViewDossierTermination"
  | "employerViewSurveyFeedback"
  | "employerViewAnalyticsActiveLeases"
  | "employerViewAnalyticsAnnualSavings"
  | "employerViewAnalyticsPayrollTaxSavings"
  | "employerViewAnalyticsVehicleSettlementSurveyResponses"
  | "employerViewAnalyticsSavingsSummaries"
  | "employerViewAnalyticsPayrollTaxSavingsSummaries"
  | "viewDossiers"
  | "viewDossier"
  | "viewDossierFromStatus"
  | "viewOpportunity"
  | "dossierEmployerSetup"
  | "dossierFinanceApplication"
  | "dossierPackagingAgreement"
  | "dossierVehicleSourcingMethod"
  | "dossierVehicleSourcingSelectVehicle"
  | "dossierVehicleSourcingConfirmVehicle"
  | "dossierVehicleSourcingOptions"
  | "dossierVehicleSourcingManufacturerOptions"
  | "dossierVehicleSourcingConfirmConfiguration"
  | "dossierDirectDebit"
  | "dossierActionConfirmation"
  | "dossierActivated"
  | "dossierTerminate"
  | "dossierViewTermination"
  | "dossierExpenseClaims"
  | "createDossierExpenseClaim"
  | "latestDossierRedirect"
  | "dossierTransactions"
  | "dossierTransactionComparison"
  | "dossierInvoiceComparison"
  | "dossierBudgets"
  | "dossierBudget"
  | "dossierBalances"
  | "dossierDocuments"
  | "dossierVehicleDetail"
  | "dossierVehicleReview"
  | "dossierVehicleSearch"
  | "dossierVehicleSearchResults"
  | "dossierVehicleComparison"
  | "compareQuotes"
  | "viewQuote"
  | "viewQuoteVehicleSuggestions"
  | "viewQuoteCalculations"
  | "viewQuoteComparisons"
  | "viewQuoteFinanceComparison"
  | "createQuote"
  | "createQuoteRedirect"
  | "editQuote"
  | "cloneQuote"
  | "personaliseQuote"
  | "requestDealerPricing"
  | "selectDealerPricing"
  | "searchDealerPricingRequests"
  | "viewSurvey"
  | "viewKeycloakUser"
  | "viewContactDossiers"
  | "crmLinkRouter"
  | "modalFaq"
  | "modalAppLocation"
  | "modalAppLocationFaq"
  | "modalEnableMFA"
  | "modalContactHelp";

export type PageConfigName = "dossierMaxQuoteCount";

export type PageDirectory = Record<
  PageDirectoryEntryName,
  PageDirectoryEntry | undefined
>;

export type PageConfigurations = Record<PageConfigName, unknown>;

type PageRouteMap = {
  internal?: PageDirectoryEntry;
  external?: PageDirectoryEntry;
  shared?: PageDirectoryEntry;
};

type PageConfigMap = {
  internal?: unknown;
  external?: unknown;
  shared?: unknown;
};

const pageRoutes: Record<PageDirectoryEntryName, PageRouteMap> = {
  home: {
    internal: () => `/internal/home`,
    external: () => `/home`,
  },
  initialUser: {
    external: () => `/user`,
  },
  userDirectDebit: {
    external: () => `/user/direct-debit`,
  },
  employerHome: {
    external: () => `/employer-home`,
  },
  employerApproveDrivers: {
    external: () => `/employer-approve-drivers`,
  },
  employerInvoices: {
    external: () => `/employer-invoices`,
  },
  employerPackagingAgreements: {
    external: () => `/employer-packaging-agreements`,
  },
  employerPayrollAdvice: {
    external: () => `/employer-payroll-advices`,
  },
  employerFBTReports: {
    external: () => `/employer-fbt-reports`,
  },
  employerEngagementReports: {
    external: () => `/employer-engagement-reports`,
  },
  employerPromotions: {
    external: () => `/employer-promotions`,
  },
  employerLuxuryLeaseReports: {
    external: () => `/employer-luxury-lease-reports`,
  },
  employerViewDossiers: {
    external: () => `/employer-dossiers`,
  },
  employerTerminateDossier: {
    external: (dossierId?: string) =>
      `/employer-dossiers/${dossierId}/terminate`,
  },
  employerViewDossierTermination: {
    external: (dossierId?: string) =>
      `/employer-dossiers/${dossierId}/termination`,
  },
  employerViewSurveyFeedback: {
    external: () => `/employer-survey-feedback`,
  },
  employerViewAnalyticsActiveLeases: {
    external: () => `/employer-analytics/active-leases`,
  },
  employerViewAnalyticsAnnualSavings: {
    external: () => `/employer-analytics/annual-savings`,
  },
  employerViewAnalyticsPayrollTaxSavings: {
    external: () => `/employer-analytics/payroll-tax-savings`,
  },
  employerViewAnalyticsVehicleSettlementSurveyResponses: {
    external: () =>
      `/employer-analytics/vehicle-settlement-survey-responses`,
  },
  employerViewAnalyticsSavingsSummaries: {
    external: () => `/employer-analytics/savings-summaries`,
  },
  employerViewAnalyticsPayrollTaxSavingsSummaries: {
    external: () => `/employer-analytics/payroll-tax-savings-summaries`,
  },
  viewDossiers: {
    external: () => `/dossiers`,
  },
  viewDossier: {
    internal: (dossierId?: string) => `/internal/dossiers/${dossierId}`,
    external: (dossierId?: string) => `/dossiers/${dossierId}`,
  },
  viewDossierFromStatus: {
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/status-redirect`,
    external: (dossierId?: string) => `/dossiers/${dossierId}/status-redirect`,
  },
  viewOpportunity: {
    internal: (opportunityId?: string) =>
      `/internal/opportunities/${opportunityId}`,
  },
  dossierEmployerSetup: {
    external: (dossierId?: string) => `/dossiers/${dossierId}/employer-setup`,
  },
  dossierFinanceApplication: {
    external: (dossierId?: string) =>
      `/dossiers/${dossierId}/finance-application`,
  },
  dossierPackagingAgreement: {
    external: (dossierId?: string) =>
      `/dossiers/${dossierId}/packaging-agreement`,
  },
  dossierVehicleSourcingMethod: {
    external: (dossierId?: string) =>
      `/dossiers/${dossierId}/vehicle-sourcing-method`,
  },
  dossierVehicleSourcingSelectVehicle: {
    external: (dossierId?: string) =>
      `/dossiers/${dossierId}/vehicle-sourcing-select-vehicle`,
  },
  dossierVehicleSourcingConfirmVehicle: {
    external: (dossierId?: string) =>
      `/dossiers/${dossierId}/vehicle-sourcing-confirm-vehicle`,
  },
  dossierVehicleSourcingOptions: {
    external: (dossierId?: string) =>
      `/dossiers/${dossierId}/vehicle-sourcing-options`,
  },
  dossierVehicleSourcingManufacturerOptions: {
    external: (dossierId?: string) =>
      `/dossiers/${dossierId}/vehicle-sourcing-manufacturer-options`,
  },
  dossierVehicleSourcingConfirmConfiguration: {
    external: (dossierId?: string) =>
      `/dossiers/${dossierId}/vehicle-sourcing-confirm-configuration`,
  },
  dossierDirectDebit: {
    external: (dossierId?: string) => `/dossiers/${dossierId}/direct-debit`,
  },
  dossierActionConfirmation: {
    external: (dossierId?: string, eventId?: string) =>
      `/dossiers/${dossierId}/action-confirmation/${eventId}`,
  },
  dossierActivated: {
    external: (dossierId?: string) => `/dossiers/${dossierId}/activated`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/activated`,
  },
  dossierTerminate: {
    external: (dossierId?: string) => `/dossiers/${dossierId}/terminate`,
  },
  dossierViewTermination: {
    external: (dossierId?: string) => `/dossiers/${dossierId}/termination`,
  },
  dossierExpenseClaims: {
    external: (dossierId?: string) => `/dossiers/${dossierId}/expense-claims`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/expense-claims`,
  },
  createDossierExpenseClaim: {
    external: (dossierId?: string) =>
      `/dossiers/${dossierId}/expense-claim/create`,
  },
  latestDossierRedirect: {
    external: (destination?: string) => `/latest-dossier/${destination}`,
  },
  dossierTransactions: {
    external: (dossierId?: string) => `/dossiers/${dossierId}/transactions`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/transactions`,
  },
  dossierTransactionComparison: {
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/transaction-comparison`,
  },
  dossierInvoiceComparison: {
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/invoice-comparison`,
  },
  dossierBudgets: {
    external: (dossierId?: string) => `/dossiers/${dossierId}/budgets`,
    internal: (dossierId?: string) => `/internal/dossiers/${dossierId}/budgets`,
  },
  dossierBudget: {
    external: (dossierId?: string, budgetId?: string) =>
      `/dossiers/${dossierId}/budgets/${budgetId}`,
    internal: (dossierId?: string, budgetId?: string) =>
      `/internal/dossiers/${dossierId}/budgets/${budgetId}`,
  },
  dossierBalances: {
    external: (dossierId?: string) => `/dossiers/${dossierId}/balances`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/balances`,
  },
  dossierDocuments: {
    external: (dossierId?: string) => `/dossiers/${dossierId}/documents`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/documents`,
  },
  dossierVehicleDetail: {
    external: (dossierId?: string, vehicleCode?: string) =>
      `/dossiers/${dossierId}/vehicle-detail/${vehicleCode}`,
    internal: (dossierId?: string, vehicleCode?: string) =>
      `/internal/dossiers/${dossierId}/vehicle-detail/${vehicleCode}`,
  },
  dossierVehicleReview: {
    external: (dossierId?: string, vehicleCode?: string) =>
      `/dossiers/${dossierId}/vehicle-review/${vehicleCode}`,
    internal: (dossierId?: string, vehicleCode?: string) =>
      `/internal/dossiers/${dossierId}/vehicle-review/${vehicleCode}`,
  },
  dossierVehicleSearch: {
    external: (dossierId?: string) => `/dossiers/${dossierId}/vehicle-search`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/vehicle-search`,
  },
  dossierVehicleSearchResults: {
    external: (dossierId?: string) =>
      `/dossiers/${dossierId}/vehicle-search-results`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/vehicle-search-results`,
  },
  dossierVehicleComparison: {
    external: (dossierId?: string) =>
      `/dossiers/${dossierId}/vehicle-comparison`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/vehicle-comparison`,
  },
  compareQuotes: {
    external: (dossierId?: string) => `/dossiers/${dossierId}/compare-quotes`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/compare-quotes`,
  },
  viewQuoteComparisons: {
    external: (dossierId?: string) =>
      `/dossiers/${dossierId}/quote-comparisons`,
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/quote-comparisons`,
  },
  viewQuote: {
    internal: (quoteId?: string) => `/internal/quote/${quoteId}`,
    external: (quoteId?: string) => `/quote/${quoteId}`,
  },
  viewQuoteVehicleSuggestions: {
    internal: (quoteId?: string) =>
      `/internal/quote/${quoteId}/vehicle-suggestions`,
    external: (quoteId?: string) => `/quote/${quoteId}/vehicle-suggestions`,
  },
  viewQuoteCalculations: {
    internal: (quoteId?: string) => `/internal/quote/${quoteId}/calculations`,
  },
  viewQuoteFinanceComparison: {
    internal: (quoteId?: string) =>
      `/internal/quote/${quoteId}/finance-comparison`,
    external: (quoteId?: string) => `/quote/${quoteId}/finance-comparison`,
  },
  createQuote: {
    internal: (dossierId?: string) =>
      `/internal/dossiers/${dossierId}/quote/create`,
    external: (dossierId?: string) => `/dossiers/${dossierId}/quote/create`,
  },
  createQuoteRedirect: {
    external: () => `/create-quote`,
  },
  editQuote: {
    internal: (quoteId?: string) => `/internal/quote/${quoteId}/edit`,
    external: (quoteId?: string) => `/quote/${quoteId}/edit`,
  },
  cloneQuote: {
    internal: (quoteId?: string) => `/internal/quote/${quoteId}/clone`,
    external: (quoteId?: string) => `/quote/${quoteId}/clone`,
  },
  personaliseQuote: {
    internal: (quoteId?: string) => `/internal/quote/${quoteId}/personalise`,
    external: (quoteId?: string) => `/quote/${quoteId}/personalise`,
  },
  requestDealerPricing: {
    internal: (quoteId?: string) =>
      `/internal/quote/${quoteId}/request-dealer-pricing`,
  },
  selectDealerPricing: {
    internal: (quoteId?: string) =>
      `/internal/quote/${quoteId}/select-dealer-pricing`,
  },
  searchDealerPricingRequests: {
    internal: () => "/internal/dealer-pricing-requests/search",
  },
  viewSurvey: {
    external: (surveyId?: string) => `/survey/${surveyId}`,
  },
  viewKeycloakUser: {
    internal: (contactId?: string) =>
      `/internal/contact/${contactId}/keycloak-user`,
  },
  viewContactDossiers: {
    internal: (contactId?: string) => `/internal/contact/${contactId}/dossiers`,
  },
  crmLinkRouter: {
    internal: (destinationId?: string) => `/internal/crm-link/${destinationId}`,
  },
  modalFaq: {
    shared: (faqId?: string) => `/modal/faq/${faqId}`,
  },
  modalAppLocation: {
    shared: (applocationId?: string) => `/modal/app-location/${applocationId}`,
  },
  modalAppLocationFaq: {
    shared: (applocationId?: string, faqId?: string) =>
      `/modal/app-location/${applocationId}/faq/${faqId}`,
  },
  modalEnableMFA: {
    external: () => `/modal/enable-mfa`,
  },
  modalContactHelp: {
    external: () => `/modal/contact-help`,
  },
};

const pageConfigurations: Record<PageConfigName, PageConfigMap> = {
  dossierMaxQuoteCount: {
    internal: 1000,
    external: 6,
  },
};

function getRouteByType(
  entryName: PageDirectoryEntryName,
  applicationScope: ApplicationScope
) {
  return (
    pageRoutes[entryName][applicationScope] || pageRoutes[entryName]["shared"]
  );
}

function getConfigByName(
  configName: PageConfigName,
  applicationScope: ApplicationScope
) {
  return (
    pageConfigurations[configName][applicationScope] ||
    pageConfigurations[configName]["shared"] ||
    undefined
  );
}

export const buildPageDirectory = (
  applicationType: ApplicationScope
): PageDirectory => {
  return (Object.keys(pageRoutes) as PageDirectoryEntryName[]).reduce(
    (directory, entryName: PageDirectoryEntryName) => {
      directory[entryName] = getRouteByType(entryName, applicationType);
      return directory;
    },
    {} as PageDirectory
  );
};

export const buildPageConfigurations = (
  applicationType: ApplicationScope
): PageConfigurations => {
  return (Object.keys(pageConfigurations) as PageConfigName[]).reduce(
    (configurations, configName: PageConfigName) => {
      configurations[configName] = getConfigByName(configName, applicationType);
      return configurations;
    },
    {} as PageConfigurations
  );
};
